import { isEqual, map } from 'lodash'
import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { Icon } from '@/common/components/Icon'
import {
  Checkbox,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui'
import { Orders } from '../../../common/models'
import { OrderPaymentStatus } from '../../../common/models/orders'
import { IColumn, Table } from '../../../core/components/Table'
import { formatDateTime } from '../../../core/constants/times'

export interface OrderTableProps {
  orders: Orders[]
  sortBy?: string
  sortDirection?: 'DESC' | 'ASC'
  setSortDirection: (s?: 'DESC' | 'ASC') => void
  setSortBy: (sort?: string) => void
  handleSearchParams?: (col: IColumn, query: string | null) => void
  loading?: boolean
  noDataMessage?: string | JSX.Element
  hasEditPermission?: boolean
  maxHeight?: string
}

export const getStatusClass = (payment: OrderPaymentStatus) => {
  switch (payment) {
    case 'PAID':
    case 'REDEEMED':
    case 'PAYMENT_NOT_NEEDED':
      return 'text-monochrome-1'
    case 'FAILED':
    case 'CANCELED':
    case 'CHECKIN_FAILED':
    case 'INSTALLMENTS_FAILED':
      return 'text-accent-3'
    case 'REFUNDED':
      return 'text-cyan-500'
    case 'PAYMENT_PENDING':
    case 'INSTALLMENTS_ON_GOING':
      return 'text-accent-4'
  }
}

export const getIcon = (payment: OrderPaymentStatus) => {
  switch (payment) {
    case 'PAID':
    case 'REDEEMED':
    case 'PAYMENT_NOT_NEEDED':
      return (
        <Icon
          icon="check"
          width="20"
          height="20"
          viewBox="0 0 20 20"
          className="text-accent-1"
          fill="none"
        />
      )
    case 'FAILED':
    case 'CANCELED':
    case 'CHECKIN_FAILED':
    case 'INSTALLMENTS_FAILED':
      return (
        <Icon
          icon="close-circle"
          width="20"
          height="20"
          viewBox="0 0 20 20"
          className="text-accent-3"
          fill="none"
        />
      )
    case 'REFUNDED':
      return (
        <>
          <Icon
            icon="return"
            width="20"
            height="20"
            viewBox="0 0 20 20"
            className="text-cyan-500"
            fill="none"
          />
        </>
      )
    case 'PAYMENT_PENDING':
    case 'INSTALLMENTS_ON_GOING':
      return (
        <Icon
          icon="clock"
          width="20"
          height="20"
          viewBox="0 0 20 20"
          className="text-accent-4"
          fill="none"
        />
      )
  }
}

const getPaymentState = (row: Orders) => {
  switch (row.paymentStatus) {
    case 'PAID':
      return 'Paid'
    case 'PAYMENT_NOT_NEEDED':
      return 'Payment Not Required'
    case 'FAILED':
      return 'Failed'
    case 'CANCELED':
      return 'Canceled'
    case 'REFUNDED':
      return 'Refunded'
    case 'REDEEMED':
      return 'Redeemed'
    case 'CHECKIN_FAILED':
      return 'Checkin Failed'
    case 'INSTALLMENTS_FAILED':
      return 'Installments Failed'
    case 'INSTALLMENTS_ON_GOING':
      return 'Installments On Going'
    case 'PAYMENT_PENDING':
      return 'Payment Pending'
    default:
      return row.paymentStatus
  }
}

const getPaymentStatus = (status: string) => {
  const split = status.split('_')
  map(split, (str, index) => {
    split[index] = split[index][0] + split[index].substring(1).toLowerCase()
  })
  return split.join(' ')
}

const PaymentCell = ({ row }: { row: Orders }) => {
  const label = row.payFullPrice ? 'Full Payment' : 'Monthly Payments'
  return (
    <div className="flex items-center gap-1.5 whitespace-nowrap">
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild className="hover:[&_svg]:text-accent-1">
            <button type="button" className="flex items-center gap-1.5">
              <Icon
                icon="info-2"
                width="18"
                height="18"
                viewBox="0 0 18 18"
                fill="none"
              />
              <span>{label}</span>
            </button>
          </TooltipTrigger>
          <TooltipContent
            side="bottom"
            className="flex flex-col gap-1.5 w-[222px]"
          >
            <div className="flex items-center gap-1.5">
              {getIcon(row.paymentStatus)}
              <span
                className={`text-button-m ${getStatusClass(row.paymentStatus)}`}
              >
                {getPaymentStatus(row.paymentStatus)}
              </span>
            </div>
            {row.paymentReason && (
              <span className="text-button-m text-monochrome-1 break-all whitespace-normal">
                {row.paymentReason}
              </span>
            )}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    </div>
  )
}

export const OrderTable = (props: OrderTableProps): JSX.Element => {
  const navigate = useNavigate()
  const [isCheckAll, setIsCheckAll] = useState(false)
  const [isCheck, setIsCheck] = useState<string[]>([])
  const { festivalName, editionId } = useParams()

  const handleSelectAll = () => {
    if (!props.hasEditPermission) {
      return
    }
    setIsCheckAll(!isCheckAll)
    setIsCheck(props.orders.map((ord) => ord.id))
    if (isCheckAll) {
      setIsCheck([])
    }
  }

  const { sortBy, setSortBy, sortDirection, setSortDirection } = props

  const handleClick = (id: string, checked: boolean) => {
    if (!props.hasEditPermission) {
      return
    }
    setIsCheck([...isCheck, id])
    if (!checked) {
      setIsCheck(isCheck.filter((item) => item !== id))
    }
  }

  const handleSortBy = (sort: string) => {
    if (sort === sortBy) {
      if (sortDirection === 'DESC') {
        setSortDirection('ASC')
      } else {
        setSortDirection(undefined)
        setSortBy(undefined)
      }
    } else {
      setSortDirection('DESC')
      setSortBy(sort)
    }
  }

  const columns: IColumn[] = [
    {
      title: (
        <Checkbox
          onCheckedChange={handleSelectAll}
          checked={isCheckAll}
          disabled={!props.hasEditPermission}
        />
      ),
      accessor: '',
      render: (row) => (
        <Checkbox
          onCheckedChange={(checked: boolean) => handleClick(row.id, checked)}
          checked={isCheck.includes(row.id)}
          disabled={!props.hasEditPermission}
        />
      ),

      width: '16px',
    },
    {
      title: 'Id',
      searchable: true,
      accessor: 'displayableOrderNumber',
      render: (row) => (
        <div className="flex flex-col whitespace-nowrap">
          <span>{row.id}</span>
          <span>
            <b>Order Number:</b> {row.displayableOrderNumber}
          </span>
          <span>
            <b>From:</b> {row.agent !== null ? row.agent : 'N/A'}
          </span>
        </div>
      ),
    },
    {
      title: 'Barcodes',
      searchable: true,
      accessor: 'barcode',
      render: (row) => (
        <div className="flex flex-col">
          {map(row.barcodes, (barcode, index) => (
            <span key={`${index}-${barcode}`}>{barcode}</span>
          ))}
        </div>
      ),
    },
    {
      title: 'Buyer',
      searchable: true,
      accessor: 'buyer',
      render: (row) => (
        <div className="flex flex-col">
          <span>{row.buyer.name}</span>
          <span>{row.buyer.phoneNumber}</span>
          <span>{row.buyer.email}</span>
        </div>
      ),
    },
    {
      title: 'Price',
      accessor: '',
      render: (row) => (
        <div className="flex flex-col">
          <span>
            {row.price.value} {row.price.currency}
          </span>
          <span className="whitespace-nowrap">{row.price.runa} RUNA</span>
        </div>
      ),
    },
    {
      title: 'Purchase Date',
      accessor: 'purchaseDate',
      sortable: true,
      render: (row) => (
        <span className="whitespace-nowrap">
          {formatDateTime(row.purchaseDate)}
        </span>
      ),
    },
    {
      title: 'Tickets',
      accessor: 'tickets',
      render: (row: Orders) => (
        <div className="flex flex-col gap-2.5 min-w-[150px]">
          {row.tickets.map((ticket) => (
            <span
              key={ticket.categoryName}
              className="text-body-medium text-monochrome-1/50"
            >
              {ticket.ticketName}
            </span>
          ))}
        </div>
      ),
    },
    {
      title: 'Tag',
      accessor: 'tag',
      render: (row: Orders) => (
        <div className="flex flex-col">
          <span>{row.voucherCode}</span>
        </div>
      ),
    },
    {
      title: 'Payment Status',
      accessor: 'paymentStatus',
      sortable: true,
      render: (row) => (
        <div className="flex items-center gap-1 whitespace-nowrap">
          {getIcon(row.paymentStatus)}
          <span className={getStatusClass(row.paymentStatus)}>
            {isEqual(row.paymentStatus, 'INSTALLMENTS_ON_GOING')
              ? 'Scheduled Payment'
              : getPaymentStatus(row.paymentStatus)}
          </span>
        </div>
      ),
    },
    {
      title: 'Payment',
      accessor: '',
      render: (row) => <PaymentCell row={row} />,
    },
    {
      title: 'View',
      accessor: '',
      render: (row) => (
        <button
          onClick={() => {
            navigate(`/orders/${festivalName}/${editionId}/${row.id}`)
          }}
          className="view text-accent-1 underline cursor-pointer"
          style={{ cursor: 'pointer' }}
        >
          View
        </button>
      ),
    },
  ]

  return (
    <Table
      columns={columns}
      data={props.orders}
      handleSortBy={handleSortBy}
      handleSearch={props.handleSearchParams}
      loading={props.loading}
      noDataMessage={props.noDataMessage}
      className="[&_th]:!whitespace-nowrap"
      maxHeight={props.maxHeight}
    />
  )
}
